import { Badge, Group, Stack, Text } from '@mantine/core';
import type {
  CalendlyBookingHealth,
  CalendlyDurationHealth,
} from '../../types';
import {
  durationRows,
  isLimitedPlan,
  limitedPlanNote,
  mappedLengths,
  twentyMinutePrompt,
} from './calendlyDurations';

interface CalendlyDurationListProps {
  durations: CalendlyDurationHealth[];
  planStage: CalendlyBookingHealth['plan_stage'];
  /** QA round 6: 'coach' speaks to the coach. 'admin' speaks about the coach. */
  audience?: 'coach' | 'admin';
  /** QA round 7: the coach's 20 min event types that are off, by name. */
  offTwentyNames?: string[];
}

/**
 * MED-1365 (QA round 2): how each session length books, from the state of
 * its Calendly event type: through Calendly, or as a Google Calendar invite
 * from the Medley calendar. The coach's Connections card and the admin view
 * of the coach show the same list.
 *
 * QA round 6: a free or trial plan maps only 20 min. The list then shows the
 * 20 min row, with no count, a note for the 45 and 60 min 1:1s, and a prompt
 * when no 20 min event type is mapped.
 */
export function CalendlyDurationList({
  durations,
  planStage,
  audience = 'coach',
  offTwentyNames = [],
}: CalendlyDurationListProps) {
  const lengths: number[] = mappedLengths(planStage);
  const rows = durationRows(
    durations.filter(d => lengths.includes(d.duration_minutes)),
    planStage
  );
  const onCalendly = rows.filter(r => r.onCalendly).length;
  const limited = isLimitedPlan(planStage);
  const twentyMissing = durations.some(
    d =>
      d.duration_minutes === 20 &&
      (d.state === 'unmapped' || d.state === 'wrong_length')
  );
  return (
    <div>
      {!limited && (
        <Badge
          variant="light"
          color={onCalendly === rows.length ? 'green' : 'blue'}
          size="sm"
        >
          {onCalendly === rows.length
            ? 'All on Calendly'
            : `${onCalendly} of ${rows.length} on Calendly`}
        </Badge>
      )}
      <Stack gap={4} mt={limited ? 0 : 'xs'}>
        {rows.map(row => (
          <Group key={row.key} gap="xs" wrap="nowrap" align="flex-start">
            <Text size="xs" fw={600} w={44} style={{ flexShrink: 0 }}>
              {row.length}
            </Text>
            <Text size="xs" style={{ minWidth: 0 }}>
              {row.how ? `${row.how}: ` : ''}
              <Text span size="xs" c="dimmed">
                {row.detail}
              </Text>
            </Text>
          </Group>
        ))}
      </Stack>
      {limited && (
        <Text size="xs" c="dimmed" mt="xs">
          {limitedPlanNote(audience)}
        </Text>
      )}
      {limited && twentyMissing && (
        <Text size="xs" fw={600} mt={4}>
          {twentyMinutePrompt(planStage, audience, offTwentyNames)}
        </Text>
      )}
    </div>
  );
}
